import { Alert, Button, DatePicker, Drawer, Form, Grid, Input, Space } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Dayjs } from "dayjs";
import type { components } from "@qorakul/shared/api";
import { api } from "../app/api.ts";
import { ApiError } from "../app/apiError.ts";

type StudentDetails = components["schemas"]["StudentDetails"];

interface StudentForm {
  firstName: string;
  lastName: string;
  dateOfBirth?: Dayjs;
}

interface Props {
  familyId: string;
  /** Pre-filled surname: the family's children usually share it. */
  defaultLastName?: string | undefined;
  open: boolean;
  onClose(): void;
  onAdded(student: StudentDetails): void;
}

export function AddStudentDrawer({ familyId, defaultLastName, open, onClose, onAdded }: Props) {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<StudentForm>();
  const [serverErrors, setServerErrors] = useState<string[]>([]);

  const mutation = useMutation({
    mutationFn: async (values: StudentForm) => {
      const { data, error, response } = await api.POST("/families/{id}/students", {
        params: { path: { id: familyId } },
        body: {
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format("YYYY-MM-DD") : null,
        },
      });
      if (!data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: async (student) => {
      await queryClient.invalidateQueries({ queryKey: ["families"] });
      setServerErrors([]);
      onAdded(student);
    },
    onError: (err: unknown) => setServerErrors(err instanceof ApiError ? err.messages(t("app.error")) : [t("app.error")]),
  });

  return (
    <Drawer
      title={t("familyDetail.addStudent")}
      open={open}
      onClose={onClose}
      size={screens.md ? 480 : "100%"}
      destroyOnHidden
      extra={
        <Space>
          <Button onClick={onClose}>{t("family.cancel")}</Button>
          <Button type="primary" loading={mutation.isPending} onClick={() => form.submit()}>
            {t("family.save")}
          </Button>
        </Space>
      }
    >
      {serverErrors.length > 0 && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} role="alert" title={serverErrors.map((m) => <div key={m}>{m}</div>)} />
      )}
      <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("familyDetail.addStudentHint")} />
      <Form<StudentForm>
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ lastName: defaultLastName ?? "" }}
        onFinish={(values) => mutation.mutate(values)}
      >
        <Form.Item name="firstName" label={t("family.firstName")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <Input maxLength={100} autoFocus />
        </Form.Item>
        <Form.Item name="lastName" label={t("family.lastName")} rules={[{ required: true, message: t("family.validation.required") }]}>
          <Input maxLength={100} />
        </Form.Item>
        <Form.Item name="dateOfBirth" label={t("family.dateOfBirth")}>
          <DatePicker style={{ width: "100%" }} format="DD.MM.YYYY" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
